/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import ICoupon from "./Coupon";
import IPlan, { NewPlan } from "./Plan";
import IPrice from "./Price";
import IPriceTarget, { NewPriceTarget } from "./PriceTarget";
import IProduct, { NewProduct } from "./Product";
import ICustomer from "./Customer";
import IAdsPublication from "./AdsPublication";
import GlobalConfiguration from "./GlobalConfiguration";

import RecordsNavigation from "./RecordsNavigation";
import _SubscriptionLine from "./Subscription/Line";
import IPriceTier from "./Price/Tier";
import User from "./User";
import Role from "./Role";
import ISubscription, { GroupedLinesTargetByProduct } from "./Subscription";
import _Invoice from "./Invoice";
import _Payment from "./Payment";
import PaymentMethod from "./PaymentMethod";
import Country from "./Country";
import ModelFieldValueTranslation from "./ModelFieldValueTranslation";
import {
    BasePriceVersion, BaseTier, CouponUsage as _CouponUsage, DiscountDetails as _DiscountDetails
} from "Shared/Models";

export {
    Column,
    CustomError,
    CustomNotification,
    DateFilter,
    Field,
    I18n,
    Locale,
    PagerSorter,
    Password,
    SelectionFilter,
    TextFilter
} from "@Internal/Models";

export type Coupon = ICoupon;
export type Customer = ICustomer;
export type AdsPublication = IAdsPublication;
export type Invoice = _Invoice;
export type Payment = _Payment;
export type Plan = IPlan;
export type Price = IPrice;
export type PriceTier = IPriceTier;
export type PriceVersion = BasePriceVersion<BaseTier>;
export type PriceTarget = IPriceTarget;

export type PriceAuditAction = "Created" | "Updated" | "Deleted";

/* One entry of a price change log. "Updated" carries a Field with its Old/New values; "Created"
   and "Deleted" are summary rows (no Field). Consumed as plain backend JSON. */
export interface PriceAudit {
    Id: number;
    PriceId: number;
    PlanId?: number;
    PriceName?: string;
    Action: PriceAuditAction;
    Field?: string;
    OldValue?: string;
    NewValue?: string;
    Actor?: string;
    Date?: string;
}

/* One entry of a subscription change log. Each row is a single changed field (recurrence,
   per-plan quantity, coupon, total) with its Old/New values. Consumed as plain backend JSON. */
export interface SubscriptionAudit {
    Id: number;
    SubscriptionId: number;
    CustomerId?: number;
    Action: "Updated";
    Field?: string;
    OldValue?: string;
    NewValue?: string;
    /* Before / after billing snapshots (JSON) on snapshot rows; absent on legacy rows. */
    Before?: string;
    After?: string;
    Actor?: string;
    Date?: string;
}

/* One outcome of a subscription in a bulk price-migration run (Migrated / Failed). Grouped by RunId
   (one campaign). Reason carries the failure message. Consumed as plain backend JSON. */
export interface SubscriptionMigration {
    Id: number;
    RunId: string;
    SubscriptionId: number;
    CustomerId?: number;
    Status: "Migrated" | "Failed";
    Reason?: string;
    Actor?: string;
    /* Display labels resolved server-side: customer company name + subscription plan name(s), so
       the table reads "Plus / Acme" instead of bare ids. Absent if the record no longer exists. */
    CustomerName?: string;
    PlanName?: string;
    /* JSON billing snapshot (recurrence, per-line label + amount, total) before / after the
       migration; set on Migrated rows only. Parsed client-side to show the change detail. */
    Before?: string;
    After?: string;
    Date?: string;
}
export type Product = IProduct;
export type Subscription = ISubscription;
export type SubscriptionLine = _SubscriptionLine;
export type DiscountDetails = _DiscountDetails;
export type CouponUsage = _CouponUsage;

export {
    Country,
    GlobalConfiguration,
    GroupedLinesTargetByProduct,
    PaymentMethod,
    ModelFieldValueTranslation,
    NewPlan,
    NewPriceTarget,
    NewProduct,
    RecordsNavigation,
    Role,
    User
};
